import {
  EnvironmentProviders,
  inject,
  isDevMode,
  Provider,
} from '@angular/core';
import {
  ApolloClientOptions,
  ApolloLink,
  InMemoryCache,
  NormalizedCacheObject,
  TypePolicies,
} from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import { getTraceData as sentryGetTraceData } from '@sentry/angular';
import { provideApollo } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { FirebaseAuthService } from '../services/firebase-auth.service';
import { graphqlUri } from '../utils/api-uri';
import generatedIntrospection from './generated/introspection-result';

const typePolicies: TypePolicies = {
  StoreItem: {
    fields: {
      // todo: remove when BE supports syllabus
      syllabus: {
        read(existing) {
          if (typeof existing === 'string') {
            return existing;
          }

          return '';
        },
      },
    },
  },
};

function createApolloClientOptions(): ApolloClientOptions<NormalizedCacheObject> {
  const httpLink = inject(HttpLink);
  const authService = inject(FirebaseAuthService);
  const uri = graphqlUri();
  // const uri = 'http://localhost:4000/graphql';

  const authLink = setContext(async (operation) => {
    const skipAuth = ['SendEmailSignInLink', 'Login', 'RefreshTokens'].includes(
      operation.operationName ?? '',
    );
    // rxState token is set asynchronously via onIdTokenChanged; requests fired
    // right after register/login would race it, so fall back to the SDK's
    // current user, which Firebase sets synchronously
    const token =
      !skipAuth &&
      (authService.state.get('token') || (await authService.getToken()));

    const traceData = sentryGetTraceData();

    return {
      headers: {
        ...(token && { authorization: `Bearer ${token}` }),
        ...(traceData['sentry-trace'] && {
          'sentry-trace': traceData['sentry-trace'],
        }),
        ...(traceData['baggage'] && { baggage: traceData['baggage'] }),
      },
    };
  });

  return {
    cache: new InMemoryCache({
      possibleTypes: generatedIntrospection.possibleTypes,
      typePolicies,
    }),
    link: ApolloLink.from([
      authLink,
      httpLink.create({
        // operation name in URL makes debugging easier in the browser network tab
        uri: ({ operationName }) => `${uri}?${operationName}`,
        withCredentials: true,
      }),
    ]),
    devtools: { enabled: isDevMode() },
  };
}

export function provideGraphQL(): (EnvironmentProviders | Provider)[] {
  return [provideApollo(createApolloClientOptions)];
}
